import { NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';
import { LoginComponent } from './modules/_shared/routes/user/login/login.component';
import { AuthGuardService } from './modules/_shared/services/auth-guard.service';
import { ReloadPageComponent } from './modules/_shared/routes/reload-page/reload-page.component';
import { AppRoutes, DebugModule, NotAuthorizedComponent, PageNotFoundComponent } from '@hutsix/ngxh6';
import { InviteComponent } from './modules/_shared/routes/user/invite/invite.component';
import { ForgotPasswordComponent } from './modules/_shared/routes/user/forgot-password/forgot-password.component';
import { ResetPasswordComponent } from './modules/_shared/routes/user/forgot-password/reset-password/reset-password.component';
import { SetPasswordComponent } from './modules/_shared/routes/user/set-password/set-password.component';
import { SetPasswordGuardService } from './modules/_shared/services/set-password-guard.service';
import { PermitFinderComponent } from './modules/_shared/routes/public/permit-finder/permit-finder.component';
import { ApplyStartComponent } from './modules/_shared/routes/public/apply-start/apply-start.component';
import { RecreationChooserComponent } from './modules/_shared/routes/public/recreation-chooser/recreation-chooser.component';
import { ApplyBusinessLoginComponent } from './modules/_shared/routes/public/apply-business-login/apply-business-login.component';
import { ApplyTouristPassComponent } from './modules/_shared/routes/public/apply-tourist-pass/apply-tourist-pass.component';
import { ApplyRecreationComponent } from './modules/_shared/routes/public/apply-recreation/apply-recreation.component';
import { ApplyFriendsFamilyComponent } from './modules/_shared/routes/public/apply-friends-family/apply-friends-family.component';
import { ApplyWorkComponent } from './modules/_shared/routes/public/apply-work/apply-work.component';
import { ApplyMediaComponent } from './modules/_shared/routes/public/apply-media/apply-media.component';
import { ApplyFishingHuntingComponent } from './modules/_shared/routes/public/apply-fishing-hunting/apply-fishing-hunting.component';
import { ApplyResearchComponent } from './modules/_shared/routes/public/apply-research/apply-research.component';
import { HostConfirmComponent } from './modules/_shared/routes/public/host-confirm/host-confirm.component';
import { ViewPermitComponent } from './modules/_shared/routes/public/view-permit/view-permit.component';
import { EditPermitComponent } from './modules/_shared/routes/public/edit-permit/edit-permit.component';
import { HomeComponent } from './modules/_shared/routes/public/home/home.component';
import { PageComponent } from './modules/_shared/routes/public/page/page.component';
import { MyPermitsComponent } from './modules/_shared/routes/public/my-permits/my-permits.component';

const routes: AppRoutes = [
    { path: 'reload', component: ReloadPageComponent },
    { path: 'login', component: LoginComponent },
    // Accepting an account a staff member created - public, the token is the key.
    { path: 'invite/:token', component: InviteComponent },
    { path: 'forgot-password', component: ForgotPasswordComponent, pathMatch: 'full' },
    // Guarded so it only exists while a password is actually owed.
    { path: 'set-password', canActivate: [SetPasswordGuardService], component: SetPasswordComponent, pathMatch: 'full' },
    { path: 'forgot-password/password-reset', component: ResetPasswordComponent, pathMatch: 'full' },
    { path: 'forgot-password/password-reset/:token', component: ResetPasswordComponent },

    // Public guest permit flow (no auth)
    // The finder ("find the right permit") lives under /find; each step is a
    // deep-linkable permalink (/find/:step). The actual application forms live
    // under /apply/<type>. /apply on its own redirects into the finder.
    // "Apply for a permit" is what the nav says and what people type, so it is a
    // real page rather than a redirect to somewhere called /find. The guided
    // questions keep /find, which is what they actually do - but nothing links to
    // /find bare any more, so it lands on the chooser.
    { path: 'apply', pathMatch: 'full', component: ApplyStartComponent },
    // Public, like every other /apply route: the organisation filling this in has no
    // account, which is what they are asking for.
    { path: 'apply/business-login', pathMatch: 'full', component: ApplyBusinessLoginComponent },
    { path: 'find', pathMatch: 'full', redirectTo: 'apply' },
    { path: 'find/:step', component: PermitFinderComponent },
    { path: 'apply/tourist-pass', component: ApplyTouristPassComponent },
    // The Recreation card splits here (camping/day-use vs boat access) before the info pages.
    { path: 'apply/recreation-choose', pathMatch: 'full', component: RecreationChooserComponent },
    { path: 'apply/recreation', component: ApplyRecreationComponent },
    { path: 'apply/friends-family', component: ApplyFriendsFamilyComponent },
    { path: 'apply/work', component: ApplyWorkComponent },
    { path: 'apply/media', component: ApplyMediaComponent },
    { path: 'apply/fishing', component: ApplyFishingHuntingComponent, data: { type: 'fishing' } },
    { path: 'apply/hunting', component: ApplyFishingHuntingComponent, data: { type: 'hunting' } },
    { path: 'apply/research', component: ApplyResearchComponent },
    { path: 'permit/edit/:editToken', component: EditPermitComponent },
    { path: 'permit/:token', component: ViewPermitComponent },
    { path: 'host-confirm/:token', component: HostConfirmComponent },

    // Guest permit list. No guard: applicants have no login, so identity comes from a
    // short-lived account grant (proved by email) rather than a session. The :grant
    // form is the emailed "find my permits" link, which swaps it into storage and
    // redirects to the bare path.
    { path: 'my-permits', component: MyPermitsComponent },
    { path: 'my-permits/:grant', component: MyPermitsComponent },

    // { path: 'form/:slug', canActivate: [AuthGuardService], component: FormRouteComponent },
    // { path: 'form/:slug/:id', canActivate: [AuthGuardService], component: FormRouteComponent },

    // Public landing page (guest-first)
    { path: '', pathMatch: 'full', component: HomeComponent },

    // Business Login area - deliberately separate from /crm so TLC staff features
    // are never exposed to businesses by default.
    {
        path: 'business',
        canActivate: [AuthGuardService],
        data: { roles: ['ROLE_BUSINESS'] },
        loadChildren: () => import('./modules/business/business.module').then(m => m.BusinessModule),
    },
    {
        path: 'crm',
        canActivate: [AuthGuardService],
        data: { roles: ['ROLE_STAFF'] },
        loadChildren: () => import('./modules/crm/crm.module').then(m => m.CrmModule),
    },
    {
        path: 'account',
        canActivate: [AuthGuardService],
        data: { roles: ['ROLE_USER'] },
        loadChildren: () => import('./modules/dashboard/dashboard.module').then(m => m.DashboardModule),
    },
    {
        path: 'user-admin',
        loadChildren: () => import('./modules/user-admin/user-admin.module').then(m => m.UserAdminModule),
    },
    {
        path: 'debug',
        canActivate: [AuthGuardService],
        data: { roles: ['ROLE_ADMIN'] },
        loadChildren: () => DebugModule,
    },
    { path: 'unauthorised', canActivate: [AuthGuardService], component: NotAuthorizedComponent },
    // Information-hub pages by slug (public, no guard). Last before the catch-all, so every
    // real route above always wins; only a genuine page slug - or an unknown single-segment
    // address, which the page then reports as not found - reaches here.
    { path: ':slug', component: PageComponent },
    { path: '**', canActivate: [AuthGuardService], component: PageNotFoundComponent },
];

@NgModule({
    imports: [RouterModule.forRoot(routes)],
    exports: [RouterModule],
})
export class AppRoutingModule {}
